import { useMemo, useState } from 'react';

type ValidationRule = (value: any) => string | undefined;

interface FormState {
  [key: string]: any; // Dynamic object type for form fields
}

interface ValidationErrors {
  [key: string]: string | undefined; // Dynamic object type for errors
}
type FormSubmitHandler = (values: FormState) => void;

export interface IuseForm {
  formData: FormState;
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (event?: React.FormEvent<HTMLFormElement>) => void;
  errors: ValidationErrors;
  setFieldValue: (field: string, value: any) => void;
  reset: (val?: FormState) => void;
  isFormValid: boolean;
}
export interface FormValidationRules {
  [key: string]: ValidationRule;
}
const useForm = (
  initialState: FormState = {},
  validationRules: FormValidationRules = {},
  onSubmit?: FormSubmitHandler
): IuseForm => {
  // Form state

  const [formData, setFormData] = useState<FormState>(initialState);
  const [errors, setErrors] = useState<ValidationErrors>({});

  // Function to handle input changes
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value =
      event.target.type === 'checkbox'
        ? event.target.checked
        : event.target.value;
    setFormData({
      ...formData,
      [event.target.name]: value,
    });
    // Trigger validation on change (optional, can be on submit instead)
    validateField(event.target.name, value);
  };

  const setFieldValue = (field: string, value: any) => {
    setFormData({
      ...formData,
      [field]: value,
    });
    validateField(field, value);
  };

  // Function to validate a specific field
  const validateField = (fieldName: string, value: any) => {
    const fieldValidation = validationRules?.[fieldName];
    if (fieldValidation) {
      const errorMessage = fieldValidation(value);
      setErrors({ ...errors, [fieldName]: errorMessage });
    } else {
      setErrors({ ...errors, [fieldName]: undefined }); // Clear error for this field
    }
  };

  // Function to validate the entire form
  const validateForm = () => {
    const allErrors: ValidationErrors = {};
    Object.entries(validationRules).forEach(([fieldName, validationRule]) => {
      if (fieldName === '*') {
        Object.keys(formData).forEach((d) => {
          const errorMessage = validationRule(formData[d]);
          if (errorMessage) {
            allErrors[d] = errorMessage;
          }
        });
      } else {
        const errorMessage = validationRule(formData[fieldName]);
        if (errorMessage) {
          allErrors[fieldName] = errorMessage;
        }
      }
    });
    setErrors(allErrors);
    return Object.keys(allErrors).length === 0; // Return true if no errors
  };
  const reset = (val = initialState) => {
    setErrors({});
    setFormData(val || {});
  };

  // Function to handle form submission
  const handleSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault?.();
    if (validateForm()) {
      // Submit form data (e.g., call an API)
      onSubmit?.(formData);
      // You can also reset the form here
    } else {
      // Handle validation errors (display error messages)
      console.error('Form validation failed');
    }
  };
  const isFormValid = useMemo(
    () => Object.values(errors).filter((d) => d)?.length === 0,
    [errors]
  );

  return {
    formData,
    handleChange,
    handleSubmit,
    errors,
    setFieldValue,
    reset,
    isFormValid,
  };
};

export default useForm;
